import { Box, Stack } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { _validateEntities } from '@/api/entity/mutations';
import ErrorIcon from '@/assets/svg/ui-elements/alert-circle-red.svg';
import CheckmarkIcon from '@/assets/svg/ui-elements/check-filled-green.svg';
import LoaderIcon from '@/assets/svg/ui-elements/loader.svg';
import LockIcon from '@/assets/svg/ui-elements/lock.svg';
import { EMAIL_REGEX } from '@/constants/static';
import {
  DatePicker,
  Helper,
  Input,
  Label,
  Select,
  theme,
  Tooltip,
  Typography,
} from '@/design-system';
import useDebounce from '@/global-hooks/use-debounce';
import { useIsMobile } from '@/global-hooks/use-is-mobile';
import { PagePaths } from '@/routes/page-paths';
import { useEntitySlice } from '@/stores/selectors';
import { StepForms } from '@/stores/slices/entity-slice';
import { isValidHttpUrl } from '@/utils/http-url';

import {
  createYourEntityLabels,
  registeredAddressLabels,
  REGISTRY_SOURCED_TOOLTIP,
} from '../common';
import { FieldGrid, FormPanel, SectionHeading } from '../styled';
import { useCreateEntity } from '../use-create-entity';
import { ENTITY_FIELD_ACCESS, useEntityFieldAccess } from '../use-entity-field-access';
import { useEntityReviewMode } from '../use-entity-review-mode';
import { useRegistryCheck } from '../use-registry-check';
import { getStepFieldErrors } from '../validate-step';
import { LogoUpload } from './logo-upload';
import { RegistryCheckBanner } from './registry-check-banner';
import { UpdatedBadge } from './wizard-status-badges';

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export const CreateYourEntity = () => {
  const isMobile = useIsMobile();
  const { pathname } = useLocation();
  const touchedFields = useRef<{ [key in 'legalName' | 'displayName']?: boolean }>({});
  const loadingFieldRef = useRef<null | 'legalName' | 'displayName'>(null);

  const { stepForms, updateStepForm, validatedSteps, currentStep, registryLockedFields } =
    useEntitySlice();
  const { isLoadingRegions, regionSelectOptions, countrySelectOptions } = useCreateEntity();
  const { accessOf, deniedReasonFor } = useEntityFieldAccess();
  const registry = useRegistryCheck();
  const { forms, isChanged, highlightIf, isReviewMode } = useEntityReviewMode();
  const isLegalIdentityChanged = createYourEntityLabels.some((field) => isChanged(field.name));
  const isAddressChanged =
    registeredAddressLabels.some((field) => isChanged(field.name)) || isChanged('logoImageId');
  const country = forms.country;
  const fieldErrors = getStepFieldErrors(1, stepForms, regionSelectOptions);
  const isStepValidated = validatedSteps.includes(currentStep);
  const isCreateEntityPage = pathname === PagePaths.createEntity;

  const debouncedLegalName = useDebounce(stepForms.legalName, 1000);
  const debouncedDisplayName = useDebounce(stepForms.displayName, 1000);

  const { mutate: validateEntities } = useMutation({
    mutationFn: _validateEntities,
    onMutate: ({ fields }) => {
      const field = Object.keys(fields)[0] as 'legalName' | 'displayName';
      loadingFieldRef.current = field;
    },
    onSettled: () => {
      loadingFieldRef.current = null;
    },
  });

  const handleFirstStepChange = (field: string, value: string | File | null | Date) => {
    let updatedValue = value;

    if (field === 'identificationNumber') {
      updatedValue = (value as string)?.replace(/[^a-zA-Z0-9]/g, '');
      if (registry.status !== 'idle') {
        registry.reset();
      }
    }

    if (field === 'legalName' || field === 'displayName') {
      touchedFields.current[field] = true;
      updateStepForm({
        [field]: value as string,
        [`is${capitalize(field)}Correct`]: null,
      });
      return;
    }

    if (field === 'country') {
      registry.reset();
      updateStepForm({ country: updatedValue as string, state: null });
      return;
    }

    updateStepForm({ [field]: updatedValue });
  };

  useEffect(() => {
    if (isReviewMode) return;
    if (!stepForms.state) updateStepForm({ state: null });
  }, [country, isReviewMode]);

  useEffect(() => {
    const nameFields = [
      {
        field: 'legalName' as const,
        rawValue: stepForms.legalName,
        debounced: debouncedLegalName,
        lastValidated: stepForms.lastValidatedLegalName,
      },
      {
        field: 'displayName' as const,
        rawValue: stepForms.displayName,
        debounced: debouncedDisplayName,
        lastValidated: stepForms.lastValidatedDisplayName,
      },
    ];

    nameFields.forEach(({ field, rawValue, debounced, lastValidated }) => {
      const shouldValidate =
        touchedFields.current[field] &&
        debounced &&
        debounced === rawValue &&
        debounced !== lastValidated;

      if (shouldValidate) {
        validateEntities(
          {
            entityId: isCreateEntityPage ? null : stepForms.id,
            fields: { [field]: debounced },
            type: 'availability',
          },
          {
            onSuccess: ({ result }) => {
              updateStepForm({
                [`is${capitalize(field)}Correct`]: result?.[field],
                [`lastValidated${capitalize(field)}`]: debounced,
              });
            },
          },
        );
      }
    });
  }, [debouncedLegalName, debouncedDisplayName]);

  const renderField = (field: (typeof createYourEntityLabels)[number]) => {
    const getFieldValue = (name: string) => forms[name as keyof StepForms];

    const fieldError = fieldErrors[field.name];
    const showError = !!fieldError && isStepValidated;

    const isFieldValid =
      (field.name === 'legalName' &&
        stepForms.isLegalNameCorrect === true &&
        getFieldValue(field.name) === stepForms.lastValidatedLegalName) ||
      (field.name === 'displayName' &&
        stepForms.isDisplayNameCorrect === true &&
        getFieldValue(field.name) === stepForms.lastValidatedDisplayName);

    const isFieldInvalid =
      (field.name === 'legalName' &&
        stepForms.isLegalNameCorrect === false &&
        getFieldValue(field.name) === stepForms.lastValidatedLegalName) ||
      (field.name === 'displayName' &&
        stepForms.isDisplayNameCorrect === false &&
        getFieldValue(field.name) === stepForms.lastValidatedDisplayName);

    const isLoading = loadingFieldRef.current === field.name;

    const isRegistryLocked = registryLockedFields.includes(field.name);
    // Registry locking and permission denial both disable the field. The
    // registry tooltip stays authoritative where both apply: "sourced from the
    // registry" explains the pre-filled value, which a permission notice would
    // not.
    const isPermissionDenied = accessOf(field.name) === ENTITY_FIELD_ACCESS.denied;
    const isLocked = isRegistryLocked || isPermissionDenied;
    const lockedReason = isRegistryLocked
      ? REGISTRY_SOURCED_TOOLTIP
      : (deniedReasonFor(field.name) ?? '');
    const lockedAdornment = (
      <Tooltip side="top" title={lockedReason}>
        <Box display="flex" alignItems="center">
          <LockIcon width={16} height={16} />
        </Box>
      </Tooltip>
    );

    const isStateDisabled =
      field.name === 'state' &&
      (!country || regionSelectOptions?.options.length === 0 || isLoadingRegions);

    const dynamicOptions =
      field.name === 'country'
        ? countrySelectOptions
        : field.name === 'state'
          ? regionSelectOptions.options
          : field.options;

    if (field.component === 'text') {
      const fieldValue = String(getFieldValue(field.name) || '');
      const isEmail = field.name.toLowerCase().includes('email');
      const isUrl = field.name === 'entityWebsite';
      const isInvalidEmail =
        isEmail && fieldValue.trim().length > 0 && !EMAIL_REGEX.test(fieldValue.trim());
      const isInvalidUrl = isUrl && fieldValue.trim().length > 0 && !isValidHttpUrl(fieldValue);

      const shouldShowError = ((showError || isInvalidEmail) && isStepValidated) || isInvalidUrl;

      const helperText =
        isInvalidEmail && showError
          ? 'Please enter a valid email address'
          : isInvalidUrl
            ? 'Please enter a valid website URL'
            : showError
              ? 'Required field'
              : '';

      return (
        <Box>
          <Input
            size="lg"
            data-testid={`entity-field-${field.name}`}
            label={field.label}
            required={field.required}
            tooltip={field.tooltip}
            placeholder={field.placeholder}
            value={fieldValue}
            maxLength={isUrl ? 2048 : 100}
            disabled={isLocked}
            changed={isChanged(field.name)}
            state={shouldShowError || isFieldInvalid ? 'destructive' : 'default'}
            helperText={isUrl ? helperText : isStepValidated ? helperText : ''}
            endIcon={
              isLocked ? (
                lockedAdornment
              ) : isFieldValid ? (
                <CheckmarkIcon width={16} height={16} />
              ) : isLoading ? (
                <LoaderIcon width={16} height={16} />
              ) : null
            }
            onChange={(e) => handleFirstStepChange(field.name, e.target.value)}
          />

          {isFieldInvalid && (
            <Stack mt={`${theme.spacing[12]}px`} alignItems="center" gap={`${theme.spacing[12]}px`}>
              <Box display="flex">
                <ErrorIcon width={24} height={24} />
              </Box>
              <Typography variant="body3" color={theme.colors.text.error}>
                {field.errorText}
              </Typography>
            </Stack>
          )}
        </Box>
      );
    }

    if (field.component === 'select') {
      const isRequired =
        field.required || (field.name === 'state' && regionSelectOptions.required) || false;

      return (
        <Box data-testid={`entity-field-${field.name}`}>
          <Select
            size="lg"
            label={field.label}
            required={isRequired}
            tooltip={isLocked ? lockedReason : field.tooltip}
            placeholder={field.placeholder}
            options={dynamicOptions || []}
            value={(getFieldValue(field.name) as string) ?? null}
            onChange={(value) => handleFirstStepChange(field.name, value)}
            showSearch={field.name === 'country' || field.name === 'state'}
            state={showError ? 'destructive' : 'default'}
            helperText={showError ? 'Required field' : ''}
            disabled={isLocked || isStateDisabled}
            changed={isChanged(field.name)}
          />
        </Box>
      );
    }

    if (field.component === 'date') {
      return (
        <Box data-testid={`entity-field-${field.name}`}>
          <Label required={field.required} tooltip={isLocked ? lockedReason : undefined}>
            {field.label}
          </Label>
          <DatePicker
            fullWidth
            value={forms.registeredAt ? new Date(forms.registeredAt) : null}
            onChange={(newDate) => handleFirstStepChange(field.name, newDate ?? null)}
            placeholder={field.placeholder}
            disabled={isLocked}
            changed={isChanged(field.name)}
            error={showError}
            disableFuture
          />
          {showError && <Helper label="Required field" state="destructive" />}
        </Box>
      );
    }

    return null;
  };

  return (
    <Stack flexDirection="column" gap={`${theme.spacing[24]}px`} width="100%">
      {registry.isVisible && (
        <RegistryCheckBanner status={registry.status} onConnect={registry.connect} />
      )}

      <FormPanel $changed={highlightIf(isLegalIdentityChanged)}>
        <SectionHeading>
          <Typography variant="h6">Legal identity</Typography>
          {isLegalIdentityChanged && <UpdatedBadge />}
        </SectionHeading>
        <FieldGrid $columns={isMobile ? 1 : 2}>
          {createYourEntityLabels.map((field) => (
            <Box key={field.name}>{renderField(field)}</Box>
          ))}
        </FieldGrid>
      </FormPanel>

      <FormPanel $changed={highlightIf(isAddressChanged)}>
        <SectionHeading>
          <Typography variant="h6">Registered address and contact</Typography>
          {isAddressChanged && <UpdatedBadge />}
        </SectionHeading>
        <FieldGrid $columns={isMobile ? 1 : 3}>
          {registeredAddressLabels.map((field) => (
            <Box key={field.name}>{renderField(field)}</Box>
          ))}
        </FieldGrid>
        <LogoUpload />
      </FormPanel>
    </Stack>
  );
};
